import React, {FC, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {ProcurementAuditLogDetail, ProcurementAuditLogSummary} from '../core/_models'
import {getProcurementLogById} from '../core/_requests'
import {resolveProcurementReferenceLink} from '../core/procurementReferenceLinks'

interface ProcurementLogModalProps {
  visible: boolean
  onClose: () => void
  title?: string
  logs: ProcurementAuditLogSummary[]
  loading?: boolean
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

const formatDetailValue = (value?: string | null) => {
  if (!value) return '-'
  const trimmed = value.trim()
  if (!trimmed) return '-'
  try {
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      return JSON.stringify(JSON.parse(trimmed), null, 2)
    }
  } catch (_error) {
    // Keep original text when not valid JSON.
  }
  return value
}

const ProcurementLogModal: FC<ProcurementLogModalProps> = ({
  visible,
  onClose,
  title,
  logs,
  loading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const navigate = useNavigate()
  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({})
  const [detailById, setDetailById] = useState<Record<number, ProcurementAuditLogDetail>>({})
  const [loadingById, setLoadingById] = useState<Record<number, boolean>>({})
  const [errorById, setErrorById] = useState<Record<number, string>>({})

  useEffect(() => {
    if (!visible) {
      setExpandedIds({})
      setDetailById({})
      setLoadingById({})
      setErrorById({})
    }
  }, [visible])

  if (!visible) return null

  const toggleDetail = async (logId: number) => {
    const expanded = Boolean(expandedIds[logId])
    if (expanded) {
      setExpandedIds((prev) => ({...prev, [logId]: false}))
      return
    }

    setExpandedIds((prev) => ({...prev, [logId]: true}))
    if (detailById[logId] || loadingById[logId]) {
      return
    }

    setLoadingById((prev) => ({...prev, [logId]: true}))
    setErrorById((prev) => ({...prev, [logId]: ''}))
    try {
      const detail = await getProcurementLogById(logId)
      setDetailById((prev) => ({...prev, [logId]: detail}))
    } catch (error: any) {
      setErrorById((prev) => ({
        ...prev,
        [logId]: error?.message || 'Failed to load audit event detail',
      }))
    } finally {
      setLoadingById((prev) => ({...prev, [logId]: false}))
    }
  }

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        inset: 0,
        zIndex: 1060,
      }}
    >
      <div className='modal-dialog modal-xl modal-dialog-centered' role='document'>
        <div className='modal-content bg-white'>
          <div className='modal-header border-bottom'>
            <div>
              <h3 className='modal-title fw-bold text-dark mb-1'>Procurement Log</h3>
              <p className='text-muted mb-0 fs-7'>{title || 'Activity timeline'}</p>
            </div>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>

          <div
            className='modal-body'
            style={{maxHeight: '70vh', overflowY: 'auto'}}
            onScroll={(event) => {
              if (!hasMore || loadingMore || !onLoadMore) return
              const target = event.currentTarget
              const remaining = target.scrollHeight - target.scrollTop - target.clientHeight
              if (remaining < 120) {
                onLoadMore()
              }
            }}
          >
            {loading ? (
              <div className='text-center text-muted py-5'>Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className='text-center text-muted py-5'>No audit logs found.</div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-sm table-bordered align-middle'>
                  <thead>
                    <tr>
                      <th style={{minWidth: '170px'}}>Timestamp</th>
                      <th style={{minWidth: '130px'}}>Stage</th>
                      <th style={{minWidth: '170px'}}>User</th>
                      <th style={{minWidth: '120px'}}>Supplier View</th>
                      <th style={{minWidth: '120px'}}>Attachment</th>
                      <th style={{minWidth: '100px'}}>Items</th>
                      <th style={{minWidth: '150px'}}>Reference</th>
                      <th style={{minWidth: '320px'}}>Business Remarks / Changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const isExpanded = Boolean(expandedIds[log.id])
                      const detail = detailById[log.id]
                      const loadingDetail = Boolean(loadingById[log.id])
                      const detailError = errorById[log.id]

                      return (
                        <React.Fragment key={log.id}>
                          <tr>
                            <td>{formatDateTime(log.createdAt)}</td>
                            <td>
                              <div className='d-flex flex-column gap-1'>
                                <span className='badge bg-light-primary text-primary'>
                                  {(log.entityName || log.moduleName || '-').toString()}
                                </span>
                                <small className='text-muted'>{log.actionName || '-'}</small>
                              </div>
                            </td>
                            <td>
                              <div>{log.username || '-'}</div>
                              <small className='text-muted'>{log.roleName || '-'}</small>
                            </td>
                            <td>
                              <span className='badge bg-light-secondary text-secondary'>Unknown</span>
                            </td>
                            <td>
                              <span className='badge bg-light-secondary text-secondary'>Deferred</span>
                            </td>
                            <td>-</td>
                            <td>
                              {(() => {
                                const link = resolveProcurementReferenceLink(log)
                                if (!link) {
                                  return (
                                    <>
                                      <div>{log.referenceNo || '-'}</div>
                                      <small className='text-muted'>{log.referenceTitle || '-'}</small>
                                    </>
                                  )
                                }
                                return (
                                  <>
                                    <button
                                      type='button'
                                      className='btn btn-link p-0 text-start text-decoration-underline'
                                      onClick={() => navigate({pathname: link.pathname, search: link.search})}
                                    >
                                      {log.referenceNo || link.label}
                                    </button>
                                    <small className='text-muted d-block'>{log.referenceTitle || '-'}</small>
                                  </>
                                )
                              })()}
                            </td>
                            <td>
                              <div className='d-flex flex-column gap-2'>
                                <div style={{whiteSpace: 'pre-wrap'}}>{log.remarksPreview || '-'}</div>
                                <div>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    onClick={() => {
                                      void toggleDetail(log.id)
                                    }}
                                  >
                                    {isExpanded ? 'Hide Details' : 'View Details'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} style={{backgroundColor: '#f8fafc'}}>
                                {loadingDetail ? (
                                  <div className='text-muted py-2'>Loading event detail...</div>
                                ) : detailError ? (
                                  <div className='text-danger py-2'>{detailError}</div>
                                ) : detail ? (
                                  <div className='d-flex flex-column gap-3 py-2'>
                                    <div>
                                      <div className='fw-semibold mb-1'>Remarks</div>
                                      <pre className='mb-0 text-wrap' style={{whiteSpace: 'pre-wrap'}}>
                                        {formatDetailValue(detail.remarks)}
                                      </pre>
                                    </div>
                                    <div>
                                      <div className='fw-semibold mb-1'>Old Value</div>
                                      <pre className='mb-0 text-wrap' style={{whiteSpace: 'pre-wrap'}}>
                                        {formatDetailValue(detail.oldValue)}
                                      </pre>
                                    </div>
                                    <div>
                                      <div className='fw-semibold mb-1'>New Value</div>
                                      <pre className='mb-0 text-wrap' style={{whiteSpace: 'pre-wrap'}}>
                                        {formatDetailValue(detail.newValue)}
                                      </pre>
                                    </div>
                                  </div>
                                ) : (
                                  <div className='text-muted py-2'>No detail available.</div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!loading && loadingMore && (
              <div className='text-center text-muted py-3'>Loading more logs...</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProcurementLogModal
